import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  ArrowRight, Briefcase, Building2, Clock, Factory, MapPin, Search, Sparkles,
  GraduationCap, Network,
} from "lucide-react";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const URG_LABEL = {
  alta: { label: "Urgente", cls: "bg-[#C5A059]/15 text-[#E7CD8D] border-[#C5A059]/40" },
  media: { label: "Regular", cls: "bg-white/5 text-white/70 border-white/15" },
  baixa: { label: "Planejada", cls: "bg-white/5 text-white/60 border-white/10" },
};

export default function CareersPage() {
  const [vacancies, setVacancies] = useState([]);
  const [filters, setFilters] = useState({ sectors: [], companies: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sectorFilter, setSectorFilter] = useState("all");
  const [companyFilter, setCompanyFilter] = useState("all");

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const params = {};
        if (sectorFilter !== "all") params.sector = sectorFilter;
        if (companyFilter !== "all") params.company = companyFilter;
        if (search.trim()) params.search = search.trim();
        const [vacs, flt] = await Promise.all([
          axios.get(`${API_BASE}/public/vacancies`, { params }),
          axios.get(`${API_BASE}/public/vacancies/filters`),
        ]);
        setVacancies(vacs.data || []);
        setFilters(flt.data || { sectors: [], companies: [], total: 0 });
      } catch (e) {
        console.error(e);
        setVacancies([]);
      } finally {
        setLoading(false);
      }
    };
    const t = setTimeout(load, search ? 300 : 0);
    return () => clearTimeout(t);
  }, [search, sectorFilter, companyFilter]);

  const bySector = useMemo(() => {
    const g = {};
    (vacancies || []).forEach((v) => {
      const key = v.sector_name || "Outras áreas";
      if (!g[key]) g[key] = [];
      g[key].push(v);
    });
    return g;
  }, [vacancies]);

  return (
    <div className="min-h-screen bg-[#0B140F] text-white overflow-x-hidden" data-testid="careers-page">
      {/* Ambient glow */}
      <div aria-hidden className="pointer-events-none fixed inset-0 z-0">
        <div className="absolute -top-40 -left-40 h-[520px] w-[520px] rounded-full bg-[#234935]/40 blur-[120px]" />
        <div className="absolute top-1/3 -right-48 h-[480px] w-[480px] rounded-full bg-[#C5A059]/15 blur-[140px]" />
        <div
          className="absolute inset-0 opacity-[0.035] mix-blend-overlay"
          style={{
            backgroundImage: "radial-gradient(rgba(255,255,255,0.8) 1px, transparent 1px)",
            backgroundSize: "3px 3px",
          }}
        />
      </div>

      {/* Header */}
      <header className="relative z-10 flex items-center justify-between px-6 md:px-12 py-5">
        <Link to="/" className="flex items-center gap-3" data-testid="careers-home-link">
          <div className="grid h-11 w-11 place-items-center rounded-lg bg-gradient-to-br from-[#234935] to-[#1A231F] ring-1 ring-[#C5A059]/30 shadow-lg shadow-black/30">
            <Factory className="h-5 w-5 text-[#C5A059]" />
          </div>
          <div className="leading-tight">
            <div className="font-display text-[17px] font-semibold tracking-wide">
              TheoKratos <span className="text-[#C5A059]">RH</span>
            </div>
            <div className="text-[10px] uppercase tracking-[0.24em] text-white/50">
              Portal de Carreiras
            </div>
          </div>
        </Link>
        <Link
          to="/login"
          data-testid="careers-cta-login"
          className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.04] px-5 py-2 text-[12.5px] font-medium tracking-wide backdrop-blur-sm transition-colors hover:border-[#C5A059]/50 hover:bg-[#C5A059]/10"
        >
          Sou colaborador <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </header>

      {/* Hero */}
      <section className="relative z-10 px-6 md:px-12 pt-6 pb-14">
        <div className="max-w-[1180px] mx-auto">
          <div className="inline-flex items-center gap-2 rounded-full border border-[#C5A059]/25 bg-[#C5A059]/[0.08] px-3 py-1 text-[10.5px] uppercase tracking-[0.22em] text-[#E0C07A]">
            <Sparkles className="h-3 w-3" /> Oportunidades abertas · Grupo ALCON
          </div>
          <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-[56px] font-semibold leading-[1.04] tracking-tight max-w-[840px]">
            Sua carreira
            {" "}
            <span className="bg-gradient-to-r from-[#C5A059] to-[#E7CD8D] bg-clip-text text-transparent">
              estratégica
            </span>
            {" "}
            começa aqui.
          </h1>
          <p className="mt-5 max-w-[620px] text-[15px] leading-relaxed text-white/75">
            Trabalhe em um grupo que integra tecnologia, gestão e propósito. Descubra nossas
            vagas abertas e candidate-se em minutos — 100% online, sem burocracia.
          </p>

          {/* Stats */}
          <div className="mt-10 grid grid-cols-3 gap-3 max-w-[560px]">
            <StatBadge icon={Briefcase} label="Vagas abertas" value={filters.total} />
            <StatBadge icon={Building2} label="Empresas" value={filters.companies.length} />
            <StatBadge icon={Network} label="Áreas" value={filters.sectors.length} />
          </div>
        </div>
      </section>

      {/* Filter bar */}
      <section className="relative z-10 px-6 md:px-12">
        <div className="max-w-[1180px] mx-auto rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-sm p-5 md:p-6">
          <div className="grid md:grid-cols-[1fr_auto_auto] gap-3 items-stretch">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-white/50" />
              <input
                data-testid="careers-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar por cargo, área ou palavra-chave..."
                className="w-full pl-11 pr-4 h-11 rounded-lg bg-[#0B140F]/70 border border-white/10 text-[13.5px] text-white placeholder:text-white/40 focus:outline-none focus:border-[#C5A059]/50"
              />
            </div>
            <select
              data-testid="careers-filter-sector"
              value={sectorFilter}
              onChange={(e) => setSectorFilter(e.target.value)}
              className="h-11 rounded-lg bg-[#0B140F]/70 border border-white/10 px-3 text-[13px] text-white focus:outline-none focus:border-[#C5A059]/50 min-w-[180px]"
            >
              <option value="all">Todas as áreas</option>
              {filters.sectors.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
            <select
              data-testid="careers-filter-company"
              value={companyFilter}
              onChange={(e) => setCompanyFilter(e.target.value)}
              className="h-11 rounded-lg bg-[#0B140F]/70 border border-white/10 px-3 text-[13px] text-white focus:outline-none focus:border-[#C5A059]/50 min-w-[180px]"
            >
              <option value="all">Todas as empresas</option>
              {filters.companies.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
        </div>
      </section>

      {/* List */}
      <section className="relative z-10 px-6 md:px-12 py-12">
        <div className="max-w-[1180px] mx-auto">
          {loading ? (
            <div className="grid md:grid-cols-2 gap-5">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-48 rounded-xl border border-white/10 bg-white/[0.02] animate-pulse" />
              ))}
            </div>
          ) : vacancies.length === 0 ? (
            <div className="text-center py-24 rounded-2xl border border-white/10 bg-white/[0.02]">
              <div className="mx-auto h-14 w-14 rounded-full bg-[#C5A059]/10 grid place-items-center mb-5">
                <Briefcase className="h-6 w-6 text-[#C5A059]" />
              </div>
              <div className="font-display text-2xl font-semibold">
                No momento não temos vagas abertas
              </div>
              <p className="mt-3 text-[13.5px] text-white/60 max-w-[420px] mx-auto">
                Fique de olho! Novas oportunidades são publicadas continuamente.
                Volte em breve.
              </p>
            </div>
          ) : (
            <div className="space-y-10">
              {Object.entries(bySector).map(([sectorName, list]) => (
                <div key={sectorName}>
                  <div className="flex items-baseline justify-between mb-4">
                    <div>
                      <div className="text-[10.5px] uppercase tracking-[0.24em] text-[#C5A059] font-medium">
                        Área
                      </div>
                      <h2 className="font-display text-2xl md:text-[28px] font-semibold mt-1">
                        {sectorName}
                      </h2>
                    </div>
                    <span className="text-[11px] uppercase tracking-[0.2em] text-white/40 font-mono">
                      {list.length} {list.length === 1 ? "vaga" : "vagas"}
                    </span>
                  </div>
                  <div className="grid md:grid-cols-2 gap-5">
                    {list.map((v, idx) => (
                      <VacancyCard key={v.id} v={v} index={idx} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 border-t border-white/5 px-6 md:px-12 py-8 mt-8">
        <div className="max-w-[1180px] mx-auto flex flex-col md:flex-row items-center justify-between gap-3 text-[11px] uppercase tracking-[0.18em] text-white/40">
          <div className="flex items-center gap-2">
            <Factory className="h-3.5 w-3.5 text-[#C5A059]" />
            <span>TheoKratos RH · Portal de Carreiras</span>
          </div>
          <div>© {new Date().getFullYear()} · Grupo ALCON</div>
        </div>
      </footer>

      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(14px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

function StatBadge({ icon: Icon, label, value }) {
  return (
    <div className="rounded-lg border border-white/10 bg-white/[0.03] p-3.5">
      <div className="flex items-center gap-2 text-[#C5A059]">
        <Icon className="h-3.5 w-3.5" />
        <span className="text-[9.5px] uppercase tracking-[0.22em] text-white/50">{label}</span>
      </div>
      <div className="font-display text-2xl font-semibold mt-1 tabular-nums">{value ?? 0}</div>
    </div>
  );
}

function VacancyCard({ v, index }) {
  const urg = URG_LABEL[v.urgencia] || URG_LABEL.media;
  return (
    <Link
      to={`/carreiras/${v.id}`}
      data-testid={`careers-vacancy-${v.id}`}
      className="group relative block rounded-xl border border-white/10 bg-gradient-to-br from-white/[0.04] to-white/[0.01] p-6 backdrop-blur-sm transition-all duration-300 hover:border-[#C5A059]/50 hover:bg-white/[0.05] hover:-translate-y-0.5"
      style={{ animation: `fadeUp 0.5s ${index * 0.05}s both` }}
    >
      <div className="absolute inset-x-6 top-0 h-px bg-gradient-to-r from-transparent via-[#C5A059]/40 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="font-display text-[19px] font-semibold tracking-tight leading-snug">
            {v.title}
          </div>
          {v.cargo && v.cargo !== v.title && (
            <div className="mt-1 text-[12.5px] text-white/55">{v.cargo}</div>
          )}
        </div>
        <span className={`shrink-0 inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[10px] uppercase tracking-[0.15em] ${urg.cls}`}>
          {urg.label}
        </span>
      </div>

      {v.descricao && (
        <p className="mt-4 text-[13px] leading-relaxed text-white/65 line-clamp-3">
          {v.descricao}
        </p>
      )}

      <div className="mt-5 flex flex-wrap gap-x-4 gap-y-1.5 text-[11.5px] text-white/60">
        {v.sector_name && (
          <span className="inline-flex items-center gap-1.5">
            <Network className="h-3 w-3 text-[#C5A059]" /> {v.sector_name}
          </span>
        )}
        {v.company_name && (
          <span className="inline-flex items-center gap-1.5">
            <Building2 className="h-3 w-3 text-[#C5A059]" /> {v.company_name}
          </span>
        )}
        {v.local_trabalho && (
          <span className="inline-flex items-center gap-1.5">
            <MapPin className="h-3 w-3 text-[#C5A059]" /> {v.local_trabalho}
          </span>
        )}
        {v.tipo_contrato && (
          <span className="inline-flex items-center gap-1.5">
            <Clock className="h-3 w-3 text-[#C5A059]" /> {v.tipo_contrato}
          </span>
        )}
        {v.escolaridade && (
          <span className="inline-flex items-center gap-1.5">
            <GraduationCap className="h-3 w-3 text-[#C5A059]" /> {v.escolaridade}
          </span>
        )}
      </div>

      <div className="mt-5 flex items-center justify-between">
        <span className="text-[11px] uppercase tracking-[0.2em] text-white/40 font-mono">
          {v.quantidade > 1 ? `${v.quantidade} posições` : "1 posição"}
        </span>
        <span className="inline-flex items-center gap-1.5 text-[12.5px] font-medium text-[#C5A059] transition-transform group-hover:translate-x-0.5">
          Ver & candidatar <ArrowRight className="h-3.5 w-3.5" />
        </span>
      </div>
    </Link>
  );
}
